import { type AccessControlProvider } from '@refinedev/core';
import Cookies from 'js-cookie';

/**
 * A mapping from resource name to permission key. The key should be
 * the resource name that's used in `src/config/resources.ts`, where
 * the value should be the permission code returned from BE.
 *
 * @example
 * ```
 * const resourceToPermissionKey = {
 * // 'RESOURCE_NAME': 'PERMISSION_KEY',
 *   'customer': 'customer',
 * }
 * ```
 */
const resourceToPermissionKey: { [key: string]: string } = {
  branches: 'branches',
  orders: 'orders',
  contacts: 'contacts',
  invoices: 'invoices',
  'cp-users': 'user-management',
};

const specialPermissions = [
  'user',
  'super.admin',
] as const;

const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action, params }) => {
    if (!resource) {
      return { can: true };
    }

    if (!Object.hasOwn(resourceToPermissionKey, resource)) {
      console.warn(`Resource name "${resource}" is not mapped to a permission key.`);
      return { can: true };
    }

    const userRoles = JSON.parse(Cookies.get('user_roles') || '{}');

    // Disable all features if permission is missing
    if (Object.keys(userRoles).length === 0) {
      return { can: false };
    }

    const permissionKey = resourceToPermissionKey[resource];

    if (permissionKey.length === 0) {
      console.warn(`Resource name "${resource}" is not mapped to a permission key.`);

      return { can: true };
    }

    const permissionsIds = userRoles.permissions
      .map((permission: any) => permission.permission_id);

    const hasSpecialPermission = permissionsIds.find(
      (id: string) => specialPermissions.find(
        (code: string) => id.startsWith(code),
      ),
    );

    if (hasSpecialPermission) {
      return { can: true };
    }

    if (!action) {
      const canAccess = !!permissionsIds.find((id: string) => id.startsWith(permissionKey)) != null;

      return { can: !!canAccess };
    }

    const permission = `${permissionKey}.${action}`;
    const canAccess = permissionsIds.find((id: string) => id === permission) != null;

    return { can: !!canAccess };
  },
};

export default accessControlProvider;
